import { DUMMY_ROOT_VALUE } from 'business/constants';
import { NodeIdT, ScopeOptionsT, SelectionTreeT } from 'business/definitions';
import {
    GetNodeIdT,
    ReducedNodeIdT,
    depthFirst,
    getNode,
} from 'helpers/treeHelpers';

const colonReplace = new RegExp('::', 'g');

export const optionsToFlatTree = (options?: FilterValueT[] | null): TreeT => ({
    label: DUMMY_ROOT_VALUE,
    value: DUMMY_ROOT_VALUE,
    id: -1,

    children:
        options == null
            ? []
            : options.map((value, key) => ({
                  id: key,
                  label: value.label,
                  value: value.value,
                  key: value.key,
                  children: [],
              })),
});

export const selectionTreeToReducedNodeId = (
    selection: SelectionTreeT,
): ReducedNodeIdT => ({
    nodeId: selection.value,
    isSubtreeSelected: selection.isSubtreeSelected,
});
export const reducedNodeIdToSelectionTree = (
    selection: ReducedNodeIdT,
    tree: TreeT,
    getNodeId: GetNodeIdT,
): SelectionTreeT => {
    const node = getNode(tree, selection.nodeId, getNodeId);

    return {
        value: node.value,
        key: node.key,
        label: node.label != null ? node.label : node.value,
        isBadValue: false,
        isSubtreeSelected: selection.isSubtreeSelected,
    };
};

// todo: update opt.name => opt.value when front refacto name -> value is done
export function isValueInOptionList(
    singleValue?: string | null,
    optionsList?: FilterValueT[] | null,
): boolean {
    if (!optionsList) return true;
    return (
        // TODO StrictNullCheck : Try to remove "!"
        optionsList !== null &&
        optionsList.map((opt) => opt.value).includes(singleValue!)
    );
}

export function isValueInOptionTree(
    singleValue: string,
    optionsTree: TreeT | null | undefined,
    getNodeId: GetNodeIdT,
): boolean {
    if (!optionsTree) return true;
    const flattenTree = depthFirst(optionsTree).map(getNodeId);
    return optionsTree !== null && flattenTree.includes(singleValue);
}

const getLabelFromScopes = (
    selectedScope: string,
    scopeOptions: ScopeOptionsT[],
): string => {
    if (scopeOptions) {
        const scp = scopeOptions.find((scope) => scope.value === selectedScope);
        if (scp) return scp.label;
    }
    return '';
};

export const deserializeScope = (
    selectedScope: string,
    scopeOptions: ScopeOptionsT[],
): SelectionTreeT => ({
    value: selectedScope,
    label: getLabelFromScopes(selectedScope, scopeOptions),

    isBadValue: !scopeOptions
        .map((scp) => scp.label)
        .includes(getLabelFromScopes(selectedScope, scopeOptions)),

    isSubtreeSelected: false,
});

/**
 * WARNING: This function is a hack for the budget module.
 * The standard logic would return the value directly. However,
 * for the 'gestionnaire' field, we need to return the label.
 */
const findLabelFromList = (
    value: string,
    optionsList?: FilterValueT[] | null,
): string => {
    const label = optionsList?.find((option) => option.value == value)?.label;
    return label ? label : value;
};

export const deserializeListValue = (
    selectedValues?: string | null,
    optionsList?: FilterValueT[] | null,
    scopeOptions: ScopeOptionsT[] = [],
    shouldDisplayLabel: boolean = false,
): SelectionTreeT[] => {
    if (
        selectedValues === null ||
        selectedValues === undefined ||
        selectedValues === ''
    )
        return [];

    return selectedValues.split('|').map((value) => {
        if (value.startsWith('scope:')) {
            return deserializeScope(value, scopeOptions);
        }
        return {
            value,
            label: shouldDisplayLabel
                ? findLabelFromList(value, optionsList)
                : value,
            isBadValue: !isValueInOptionList(value, optionsList),
            isSubtreeSelected: false,
        };
    });
};

export const getScopeField = (scopeName: string) => scopeName.split(':')[1];

export const translateScopeOptionToSelectionTree = (
    scopeOption: ScopeOptionsT,
): SelectionTreeT => ({
    value: scopeOption.value,
    label: getScopeField(scopeOption.value),
    isBadValue: false,
    isSubtreeSelected: false,
});

export const getNodeIdWithKey = (node: TreeT): NodeIdT => {
    let result: string;
    if (node.key !== undefined) result = node.key;
    else result = node.value;
    return result;
};

const getLabelFromValue = (value: string, optionsTree: TreeT): string =>
    getNode(optionsTree, value, (node) => node.value).label;

const _deserializeTreeValue = (
    selectedValue: string,
    optionsTree: TreeT,
    getNodeId: GetNodeIdT,
): SelectionTreeT => {
    const value = selectedValue.replace(colonReplace, '');
    const isBad = !isValueInOptionTree(value, optionsTree, getNodeId);

    return {
        value,
        label: isBad ? value : getLabelFromValue(value, optionsTree),
        isBadValue: isBad,
        isSubtreeSelected: selectedValue.includes('::'),
    };
};

export const deserializeTreeValue = (
    selectedValues?: string | null,
    // @ts-expect-error [TS migration] Was not detected by flow
    optionsTree: TreeT,
    getNodeId: GetNodeIdT,
    scopeOptions: ScopeOptionsT[] = [],
): SelectionTreeT[] => {
    if (
        selectedValues === null ||
        selectedValues === '' ||
        selectedValues === undefined ||
        optionsTree === undefined
    )
        return [];
    return selectedValues.split('|').map((val) => {
        if (val.startsWith('scope:'))
            return deserializeScope(val, scopeOptions);
        return _deserializeTreeValue(val, optionsTree, getNodeId);
    });
};

export function serializeTreeValue(serializedValue: SelectionTreeT[]): string {
    return serializedValue
        .map((val) => (val.isSubtreeSelected ? `${val.value}::` : val.value))
        .join('|');
}

export function serializeListValue(serializedValue: SelectionTreeT[]): string {
    return serializedValue.map((val) => val.value).join('|');
}
/**
 * Get the value label pairs from a tree
 * @param tree the tree to get the value label pairs from
 * @param mapId true if we want to add the mapping between the id to the label
 * @returns the value label pairs
 */
export const getValueLabelPairsFromTree = (
    tree: TreeT,
    mapId: boolean = false,
): Record<string, string> => {
    const valueLabelPairs: Record<string, string> = {};
    const recursiveGetValueLabelPairs = (node: TreeT): void => {
        if (node.key) valueLabelPairs[node.key] = node.label;
        else valueLabelPairs[node.value] = node.label;

        // we need this because for budget we expect the id to be the key
        // to fix this, we need to ensure that the node.key is available and matches the id
        // another way would be to have the same behavior as the gestionnaire for example
        if (mapId && node.id) valueLabelPairs[node.id.toString()] = node.label;

        node.children.forEach((child: TreeT) =>
            recursiveGetValueLabelPairs(child),
        );
    };

    recursiveGetValueLabelPairs(tree);
    return valueLabelPairs;
};
